/* VariantImageEditor — Admin Overview tile.
 *
 * Lets staff paste real manufacturer CDN URLs onto each color variant of
 * any product without touching code or re-deploying. Pairs with the
 * scraper at /app/scripts/scrape_variant_images.py — when the scraper
 * can't reach a SPA configurator (Samsung's per-color SKU images), use
 * this UI to plug them in manually.
 *
 * Backend: PUT /api/store/products/by-slug/{slug}/variant-image
 *          GET /api/store/products/admin/with-color-variants
 *
 * Auth: stores the store-auth JWT in localStorage as `zhelta_store_token`
 * once the admin logs in via the in-tile sign-in. We don't reuse
 * `zhelta_admin_pw` because the variant endpoint is gated by store JWT
 * auth, not the legacy admin-password pattern.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Palette, RefreshCw, Save, Lock, ExternalLink, Check, AlertCircle, Image as ImageIcon, X, Upload,
} from 'lucide-react';

const API = `${(process.env.CRA_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL)}/api`;
const TOKEN_KEY = 'zhelta_store_token';

function getToken() {
  try { return localStorage.getItem(TOKEN_KEY) || ''; } catch { return ''; }
}
function setToken(v) {
  try { v ? localStorage.setItem(TOKEN_KEY, v) : localStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
}

async function authFetch(path, options = {}) {
  const headers = { ...(options.headers || {}) };
  const tok = getToken();
  if (tok) headers['Authorization'] = `Bearer ${tok}`;
  if (options.body && !headers['Content-Type']) headers['Content-Type'] = 'application/json';
  const res = await fetch(`${API}${path}`, { ...options, headers });
  return res;
}

// Tiny inline sign-in form — only shows if we have NO store token. Once
// signed in once per browser, this stays out of the way.
function SignInForm({ onAuthed }) {
  const [email, setEmail] = useState('admin@zhelta.com');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      const r = await fetch(`${API}/store/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase(), password }),
      });
      const d = await r.json();
      if (!r.ok || !d.token) throw new Error(d.detail || 'Login failed');
      setToken(d.token);
      onAuthed();
    } catch (e2) {
      setErr(e2.message || 'Sign-in failed');
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={onSubmit} className="px-6 py-6 flex flex-col gap-3" data-testid="variant-image-signin">
      <div className="flex items-center gap-2 text-[12px] text-gray-600">
        <Lock className="w-3.5 h-3.5 text-[#c9a227]" />
        Sign in with a store-admin account to edit variant images.
      </div>
      <input
        type="email" value={email} onChange={(e) => setEmail(e.target.value)}
        placeholder="admin@zhelta.com"
        className="px-3 py-2 rounded-md border border-gray-200 text-[13px] focus:outline-none focus:border-[#c9a227]"
        data-testid="variant-image-signin-email"
      />
      <input
        type="password" value={password} onChange={(e) => setPassword(e.target.value)}
        placeholder="••••••••"
        className="px-3 py-2 rounded-md border border-gray-200 text-[13px] focus:outline-none focus:border-[#c9a227]"
        data-testid="variant-image-signin-pw"
      />
      {err && <span className="text-[11.5px] text-rose-700 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{err}</span>}
      <button
        type="submit" disabled={busy || !password}
        data-testid="variant-image-signin-submit"
        className="px-4 py-2 rounded-full font-bold uppercase tracking-wider text-[11px] text-zinc-950 disabled:opacity-50"
        style={{ background: 'linear-gradient(90deg,#e6c35a,#c9a227)' }}
      >
        {busy ? 'Signing in…' : 'Sign in'}
      </button>
    </form>
  );
}

export default function VariantImageEditor() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [authedTick, setAuthedTick] = useState(0);
  const [activeSlug, setActiveSlug] = useState(null);

  const isAuthed = useMemo(() => !!getToken(), [authedTick]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authFetch('/store/products/admin/with-color-variants');
      if (r.status === 401) {
        setToken('');
        setAuthedTick((t) => t + 1);
        setProducts([]);
        return;
      }
      if (r.ok) setProducts(await r.json());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (isAuthed) load(); }, [isAuthed, load]);

  const stats = useMemo(() => {
    let total = 0, verified = 0;
    for (const p of products) {
      const cs = (p.variants?.color) || [];
      total += cs.length;
      verified += cs.filter((c) => c.image).length;
    }
    return { total, verified };
  }, [products]);

  return (
    <section
      data-testid="admin-variant-image-editor"
      className="rounded-2xl border border-gray-200 bg-white overflow-hidden"
    >
      <header className="px-6 py-4 flex items-center justify-between bg-[#fffbe9] border-b border-[#f0e3a8] flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Palette className="w-4 h-4 text-[#c9a227]" />
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#a8871a]">Catalog · Variant images</div>
            <h3 className="text-[15px] font-bold text-gray-900">Per-color manufacturer photos</h3>
            <p className="text-[10.5px] text-gray-500 mt-0.5">Drag a photo onto any color row, paste a URL, or click Upload.</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[11.5px] text-gray-600" data-testid="variant-image-stats">
            <strong className="text-[#a8871a]">{stats.verified}</strong> / <strong>{stats.total}</strong> color variants with photos
          </span>
          {isAuthed && (
            <button
              type="button" onClick={load} disabled={loading}
              data-testid="variant-image-refresh"
              className="p-1.5 rounded-full border border-gray-200 text-gray-500 hover:text-[#c9a227] hover:border-[#c9a227] transition-colors disabled:opacity-50"
              title="Refresh"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          )}
          {isAuthed && (
            <button
              type="button"
              onClick={() => { setToken(''); setAuthedTick((t) => t + 1); }}
              data-testid="variant-image-signout"
              className="text-[11px] text-gray-500 hover:text-rose-600"
            >
              Sign out
            </button>
          )}
        </div>
      </header>

      {!isAuthed ? (
        <SignInForm onAuthed={() => setAuthedTick((t) => t + 1)} />
      ) : products.length === 0 && !loading ? (
        <div className="text-center py-10 px-6">
          <ImageIcon className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-[13px] text-gray-500">No products with color variants yet.</p>
        </div>
      ) : (
        <div className="px-2 py-3 max-h-[680px] overflow-y-auto" data-testid="variant-image-list">
          {products.map((p) => (
            <ProductRow
              key={p.slug}
              product={p}
              expanded={activeSlug === p.slug}
              onToggle={() => setActiveSlug(activeSlug === p.slug ? null : p.slug)}
              onSaved={(updatedColors) => {
                // Optimistic in-place update so the totals + row reflect
                // the new state without a full refetch.
                setProducts((cur) => cur.map((cp) =>
                  cp.slug === p.slug
                    ? { ...cp, variants: { ...(cp.variants || {}), color: updatedColors } }
                    : cp
                ));
              }}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function ProductRow({ product, expanded, onToggle, onSaved }) {
  const colors = product.variants?.color || [];
  const verified = colors.filter((c) => c.image).length;
  const allDone = verified === colors.length;
  return (
    <div className="border-b border-gray-100 last:border-b-0" data-testid={`variant-image-row-${product.slug}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors text-left"
      >
        <img
          src={product.image} alt=""
          className="w-9 h-9 rounded-md object-cover bg-gray-100 shrink-0"
          onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
        />
        <span className="flex-1 min-w-0">
          <span className="block text-[13px] font-bold text-gray-900 truncate">{product.name}</span>
          <span className="block text-[10.5px] uppercase tracking-wider text-gray-400">{product.brand} · {product.category}</span>
        </span>
        <span
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-bold ${
            allDone ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
          }`}
        >
          {allDone ? <Check className="w-3 h-3" /> : null}
          {verified} / {colors.length}
        </span>
        <span className="text-gray-400 text-[14px] w-3 text-center">{expanded ? '−' : '+'}</span>
      </button>
      {expanded && (
        <div className="px-4 pb-4 pt-2 bg-gray-50/50 flex flex-col gap-2">
          {colors.map((c) => (
            <ColorRow
              key={c.value}
              slug={product.slug}
              color={c}
              onSaved={(updated) => {
                const next = colors.map((cc) => cc.value === c.value ? updated : cc);
                onSaved(next);
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ColorRow({ slug, color, onSaved }) {
  const [url, setUrl] = useState(color.image || '');
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [msg, setMsg] = useState('');
  const fileInputRef = useRef(null);
  const dirty = (color.image || '') !== url.trim();

  const save = async (explicitUrl) => {
    const next = typeof explicitUrl === 'string' ? explicitUrl : url.trim();
    setBusy(true); setMsg('');
    try {
      const r = await authFetch(`/store/products/by-slug/${slug}/variant-image`, {
        method: 'PUT',
        body: JSON.stringify({ color_value: color.value, image_url: next || null }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Save failed');
      const updated = { ...color };
      if (d.image_url) updated.image = d.image_url;
      else delete updated.image;
      onSaved(updated);
      if (typeof explicitUrl === 'string') setUrl(d.image_url || '');
      setMsg(d.image_url ? 'Saved ✓' : 'Cleared ✓');
      setTimeout(() => setMsg(''), 2200);
    } catch (e) {
      setMsg(e.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  // Uploads one file via multipart, then auto-saves the returned URL onto
  // this variant so the admin sees the new photo without a second click.
  const uploadFile = async (file) => {
    if (!file) return;
    if (!file.type || !file.type.startsWith('image/')) {
      setMsg('Only image files allowed');
      setTimeout(() => setMsg(''), 2500);
      return;
    }
    setUploading(true); setMsg('Uploading…');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const tok = getToken();
      const r = await fetch(`${API}/store/products/admin/uploads/variant-image`, {
        method: 'POST',
        body: fd,
        headers: tok ? { Authorization: `Bearer ${tok}` } : {},
      });
      const d = await r.json();
      if (!r.ok || !d.url) throw new Error(d.detail || 'Upload failed');
      setUrl(d.url);
      await save(d.url);
    } catch (e) {
      setMsg(e.message || 'Upload failed');
      setTimeout(() => setMsg(''), 2800);
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) uploadFile(file);
  };
  const onDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(true); };
  const onDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); };
  const onFilePicked = (e) => {
    const f = e.target.files?.[0];
    if (f) uploadFile(f);
    e.target.value = '';
  };

  const busyAny = busy || uploading;

  return (
    <div
      data-testid={`variant-image-color-${slug}-${color.value}`}
      onDrop={onDrop}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      className={`flex items-center gap-2 flex-wrap p-2 rounded-md transition-colors ${
        dragActive
          ? 'bg-amber-50 ring-2 ring-[#c9a227] ring-offset-1'
          : 'hover:bg-white/60'
      }`}
    >
      <span
        className="h-5 w-5 rounded-full border border-gray-200 shrink-0"
        style={{ background: color.hex || '#888' }}
        title={color.hex}
      />
      <span className="text-[12px] font-semibold text-gray-700 w-32 truncate">{color.label}</span>

      {/* Tiny preview thumbnail when a variant image is set — gives admins a
          quick visual confirmation the URL points to the right photo. */}
      {color.image && (
        <img
          src={color.image}
          alt=""
          className="h-8 w-8 rounded-md object-cover border border-gray-200 bg-white shrink-0"
          onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
        />
      )}

      <input
        type="url" value={url} onChange={(e) => setUrl(e.target.value)}
        placeholder={dragActive ? 'Drop to upload…' : 'https://… or drag-and-drop a photo'}
        className="flex-1 min-w-[220px] px-2.5 py-1.5 rounded-md border border-gray-200 text-[12px] font-mono focus:outline-none focus:border-[#c9a227] bg-white"
        data-testid={`variant-image-url-${color.value}`}
      />

      {/* Hidden <input type=file> drives the click-to-upload fallback for
          users who don't want to drag. */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={onFilePicked}
        className="hidden"
        data-testid={`variant-image-file-${color.value}`}
      />
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        disabled={busyAny}
        data-testid={`variant-image-upload-${color.value}`}
        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full border border-gray-200 text-gray-600 hover:text-[#c9a227] hover:border-[#c9a227] text-[10.5px] font-bold uppercase tracking-wider disabled:opacity-40 transition-colors"
        title="Upload image from computer"
      >
        <Upload className="w-3 h-3" />
        {uploading ? '…' : 'Upload'}
      </button>

      {url && (
        <a
          href={url} target="_blank" rel="noopener noreferrer"
          className="text-gray-400 hover:text-[#c9a227]"
          title="Preview URL"
        >
          <ExternalLink className="w-3.5 h-3.5" />
        </a>
      )}
      {color.image && url.trim() !== color.image && (
        <button
          type="button" onClick={() => setUrl(color.image || '')}
          className="text-gray-400 hover:text-gray-700"
          title="Revert"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}
      <button
        type="button"
        disabled={busyAny || !dirty}
        onClick={() => save()}
        data-testid={`variant-image-save-${color.value}`}
        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-zinc-950 font-bold uppercase tracking-wider text-[10.5px] disabled:opacity-40 hover:opacity-90 transition-opacity"
        style={{ background: 'linear-gradient(90deg,#e6c35a,#c9a227)' }}
      >
        <Save className="w-3 h-3" />
        {busy ? '…' : 'Save'}
      </button>
      {msg && <span className={`text-[10.5px] font-bold ${msg.includes('✓') ? 'text-emerald-700' : msg.startsWith('Upload') ? 'text-gray-600' : 'text-rose-700'}`}>{msg}</span>}
    </div>
  );
}
